import { DialogContent, DialogTitle, Tab, Tabs, Typography } from "@mui/material";
import { ChildrenCost, Costing, MaterialCost } from "pdf-generator";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../redux";
import { useLazyGetAllCostingQuery } from "../../../redux/services";
import { AssemblyProcess as AssPro } from "../../../redux/slices/bom";
import { getUserSelectedCurrency } from "../../../utils";
import { AssemblyProcess } from "../../viewBOM/assemblyProcess";
import { PricingTabs } from "../utils";
import { EditCosting } from "./editCosting";
import { FlatView } from "./flatview";
import { PreviewCostDetails } from "./preview";

export const OverheadDetails = ({ assemblyProcess }: { assemblyProcess: Array<AssPro> }) => {
    return (
        <>
            <DialogTitle>
                <Typography
                    variant="h6"
                    color="textPrimary"
                    sx={{
                        width: "100%",
                        fontFamily: 'htrts_medium',
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    Overhead/Activity Details
                </Typography>
            </DialogTitle>
            <DialogContent>
                <AssemblyProcess isViewOnly={true} hideExpandCollapse assemblyProcess={assemblyProcess} currency={assemblyProcess?.[0]?.currency?.label ?? "-"} hasNewTotalCost />
            </DialogContent>
        </>
    );
};

export const CostingDetails: React.FC<{ readOnly: boolean, refetch: () => void }> = ({ refetch, readOnly }) => {

    const [getAllCosting, costingRes] = useLazyGetAllCostingQuery();

    const { id = "" } = useParams();

    const { pricing, auth } = useAppSelector(store => store);

    const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

    const [selectedTab, updateTab] = useState(PricingTabs[0]);

    const costingData = useMemo(() => {
        const { data = [] } = costingRes;
        const latestCosting = data?.filter((d: any) => new Date(d.created_time) > new Date(pricing.modified_time));
        const mostRecentCosting: Costing = latestCosting?.reduce((recent: any, current: any) => {
            return new Date(current.created_time) > new Date(recent.created_time) ? current : recent;
        }, latestCosting[0] || {});
        const base_currency = mostRecentCosting?.pricing_config?.exchange_rates?.base_currency ?? DEFAULT_CURRENCY;

        const hasCosting = Object.keys(mostRecentCosting).length > 0;
        let masterData: (string | number)[][] = [];
        let levelsData: (string | number)[][] = [];

        if (hasCosting) {
            // Master Data Manipulation
            const includeMasterFields = [
                'total_bom_cost',
                'total_other_cost',
                'total_discount',
                'total_tax'
            ];

            if (mostRecentCosting?.margin_amount) {
                includeMasterFields.push("margin_amount");
            }

            const masterLabels: Record<any, string> = {
                'total_bom_cost': "Major Items Cost",
                'total_other_cost': "Other Items Cost",
                'total_discount': "Adjustments Cost",
                'total_tax': "Taxes",
            };

            if (mostRecentCosting?.margin_amount) {
                masterLabels["margin_amount"] = "Margin";
            }

            masterData = [
                ["Pricing Breakup", "Value"],
                ...(includeMasterFields.map((key: string) => {
                    if (key === includeMasterFields[0]) {
                        // eslint-disable-next-line
                        return [masterLabels[key], Number((Number(eval(`mostRecentCosting?.${key}`)) + mostRecentCosting?.total_cost_group_cost).toFixed(2))]
                    }
                    // eslint-disable-next-line
                    return [masterLabels[key], Number(Math.abs(eval(`mostRecentCosting?.${key}`)).toFixed(2))]
                }) ?? [])
            ]

            // Levels Data Manipulation
            levelsData.push(["BOM First Level Details", "Value"]);

            mostRecentCosting?.calculated_cost?.children_costs?.forEach((c: ChildrenCost) => {
                levelsData.push([`${c.sequence_id}-r${c.version} (${c.short_description})`, Number(c.final_total_cost_in_pricing_currency?.cost?.toFixed(2))])
            })

            mostRecentCosting?.calculated_cost?.material_costs?.forEach((c: MaterialCost) => {
                levelsData.push([`${c.sequence_id}-r${c.version} (${c.short_description})`, Number(c.final_total_cost_in_pricing_currency?.cost?.toFixed(2))])
            })
        }

        return { costing: mostRecentCosting, masterData, levelsData, hasCosting, base_currency }
        // eslint-disable-next-line
    }, [costingRes.status, pricing.modified_time])

    const fetchCosting = () => {
        getAllCosting({ bom_id: pricing.basic_details?.bom_id?.value as string, pricing_id: id });
    };

    useEffect(() => {
        fetchCosting();
        // eslint-disable-next-line
    }, [selectedTab, pricing?.modified_time])

    return <>
        <Tabs
            value={selectedTab}
            onChange={(e, tab) => updateTab(tab)}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="auto"
            variant="scrollable"
        >
            <Tab value={PricingTabs[0]} label="Hierarchy View (Edit)" />
            <Tab value={PricingTabs[1]} disabled={costingRes.isError || (costingRes.isSuccess && (costingRes.data ?? []).length <= 0)} label="Hierarchy View (preview)" />
            <Tab value={PricingTabs[2]} disabled={costingRes.isError || (costingRes.isSuccess && (costingRes.data ?? []).length <= 0)} label="Flat View (preview)" />
        </Tabs>

        {selectedTab === PricingTabs[0] && <EditCosting readOnly={readOnly} refetchCosting={fetchCosting} refetch={refetch} costingData={costingData} />}
        {selectedTab === PricingTabs[1] && <PreviewCostDetails readOnly={readOnly} refetchCosting={fetchCosting} refetch={refetch} costingData={costingData} costingRes={costingRes} />}
        {selectedTab === PricingTabs[2] && <FlatView readOnly={readOnly} refetchCosting={fetchCosting} refetch={refetch} costingData={costingData} costingRes={costingRes} />}
    </>

}